import React, { useEffect, useRef, useState } from 'react';

import { Icon, useIdCounter } from '@rbilabs/components-library';
import Measure, { MeasuredComponentProps } from 'react-measure';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';

import defaultConfig from './constants';
import { ChildrenContainer, ExpanderWrapper, ToggleButtonWrapper } from './styled';
import { useClonedChildrenWithRefs } from './use-cloned-children-with-refs';

interface IExpanderProps {
  className?: string;
  minVisibleChildren?: number;
  openText?: string;
  closedText?: string;
}

const Expander: React.FC<IExpanderProps> = ({
  closedText,
  children,
  className,
  minVisibleChildren = Infinity,
  openText,
}) => {
  const childrenContainerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [minHeight, setMinHeight] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);
  const uniqueID = useIdCounter('expander');
  const minVisible = Math.ceil(minVisibleChildren / defaultConfig.reduceVisibleBy);

  const { childrenRefs, clonedChildrenWithRefs } = useClonedChildrenWithRefs({
    children,
    isExpanded,
    minVisibleChildren,
  });

  const calculateDimensions = React.useCallback(() => {
    const minimumHeight = (childrenRefs || [])
      .filter((_, i) => (i + defaultConfig.reduceVisibleBy) % defaultConfig.reduceVisibleBy === 0)
      .slice(0, minVisible)
      .reduce((sum, childRef) => {
        if (!childRef.current || !childRef.current.firstElementChild) {
          return sum;
        }

        const elHeight = childRef.current.offsetHeight;

        /**
         * `offsetHeight` only returns the content, padding, and border,
         * so we need to manually add the margin. How margin renders is
         * complex enough (ie only the max of element1's margin-bottom and
         * element2's margin-top is rendered) that we simplify for our use-case
         * by assuming all children have the same margin style rules.
         */
        const styles = window.getComputedStyle(childRef.current.firstElementChild);
        const marginTopRaw = styles.getPropertyValue('margin-top');
        const marginTop = marginTopRaw ? parseInt(marginTopRaw, 10) : 0;
        const marginBottomRaw = styles.getPropertyValue('margin-bottom');
        const marginBottom = marginBottomRaw ? parseInt(marginBottomRaw, 10) : 0;

        return sum + elHeight + Math.max(marginTop, marginBottom);
      }, 0);

    setMinHeight(minimumHeight);

    if (childrenContainerRef.current) {
      setMaxHeight(childrenContainerRef.current.scrollHeight);
    }
  }, [childrenRefs, minVisible]);

  useEffect(() => {
    calculateDimensions();
  }, [calculateDimensions]);

  return (
    <Measure
      onResize={() => {
        /**
         * We recalculate dimensions here so we can capture changes in
         * child components (maybe they change their height or are
         * asynchronously loaded)
         */
        calculateDimensions();
      }}
    >
      {({ measureRef }: MeasuredComponentProps) => {
        return (
          <ExpanderWrapper className={className}>
            <ChildrenContainer
              id={uniqueID}
              height={isExpanded ? maxHeight : minHeight}
              ref={childrenContainerRef}
            >
              <div ref={measureRef}>{clonedChildrenWithRefs}</div>
            </ChildrenContainer>

            {clonedChildrenWithRefs && clonedChildrenWithRefs.length > minVisibleChildren && (
              <ToggleButtonWrapper isExpanded={isExpanded}>
                <ActionButton
                  size={ActionButtonSizes.SMALL}
                  variant={ActionButtonVariants.OUTLINE}
                  onClick={() => setIsExpanded(state => !state)}
                  data-testid="toggle-expander-button"
                  aria-controls={uniqueID}
                  aria-expanded={isExpanded}
                  endIcon={<Icon icon="select" color="primary" />}
                >
                  {isExpanded ? openText : closedText}
                </ActionButton>
              </ToggleButtonWrapper>
            )}
          </ExpanderWrapper>
        );
      }}
    </Measure>
  );
};

export default Expander;
